import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { QBOImportDocumentRow } from "components/QBO/QBOImportReview";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useGetFinallyImportQuery } from "store/apis/finallyImport";
import { formatDate } from "utils/formatDate";
import { FinallyWhereToFindAccordion } from "./FinallyWhereToFindAccordion";

type FinallyImportViewProps = {
  entityId: string;
  isOpen: boolean;
  onClose: () => void;
};

type ImportDateRowProps = {
  label: string;
  date: string | null;
};

const ImportDateRow = ({ label, date }: ImportDateRowProps) => (
  <div className="t-flex t-flex-col t-gap-2">
    <span className="t-text-body-sm t-text-text-30">{label}</span>
    <span className="t-text-body-sm t-text-text-100">
      {date ? formatDate(date) : "-"}
    </span>
  </div>
);

export const FinallyImportView = ({
  entityId,
  isOpen,
  onClose,
}: FinallyImportViewProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { downloadFile, result: downloadState } = useDownloadFile();

  const { data, isLoading, isSuccess, isError } = useGetFinallyImportQuery(
    { entityId },
    { skip: !entityId || !isOpen }
  );

  const handleDownloadFile = (fileId: string) => {
    void downloadFile({ file_id: fileId, group_id: groupId });
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose} modal={false}>
      <Modal.Content size="xl" useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Imported data from Finally</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Async.Root
          isLoading={isLoading}
          isSuccess={isSuccess}
          isError={isError}
          isEmpty={!data}
        >
          <Async.Empty>
            <Modal.Body className="t-text-center t-h-60 t-text-body-sm t-text-text-100 t-flex t-items-center t-justify-center">
              No import data found
            </Modal.Body>
          </Async.Empty>
          <Async.ErrorHandler>
            <Modal.Body className="t-text-center t-h-60 t-text-body-sm t-text-text-100 t-flex t-items-center t-justify-center">
              Something went wrong while loading the imported data.
            </Modal.Body>
          </Async.ErrorHandler>
          <Async.Success>
            <Modal.Body className="t-flex t-flex-col t-gap-5">
              <div className="t-grid t-grid-cols-2 t-gap-4">
                <ImportDateRow
                  label="From"
                  date={data?.prev_fy_end_date ?? null}
                />
                <ImportDateRow
                  label="To"
                  date={data?.date_till_import ?? null}
                />
              </div>
              <div className="t-text-sm t-font-medium t-mt-2">
                Documents
                <FinallyWhereToFindAccordion />
              </div>
              <QBOImportDocumentRow
                label="Chart of Accounts"
                file={data?.coa_file ?? undefined}
                isDownloading={downloadState.isFetching}
                onDownloadFile={handleDownloadFile}
              />
              <QBOImportDocumentRow
                label="Journal Entries"
                file={data?.je_file ?? undefined}
                isDownloading={downloadState.isFetching}
                onDownloadFile={handleDownloadFile}
              />
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button type="button">Close</Button>
              </Modal.RawClose>
            </Modal.FooterButtonGroup>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
